import React from 'react';
import {
  func, string, number, bool
} from 'prop-types';
import Collapsible from 'react-collapsible';
import { AVAILABLE_LANGUAGES } from '../../constants';
import RichTextEditor from '../RichTextEditor';
import styles from './localizedInput.module.css';
import TextInput from '../TextInput';
import { langType, withLang } from '../../i18n';
import { localizedType } from '../../constants/types';

const createAccordionTitle = (lang, active, translate) => {
  const activeClassName = active ? 'is-active' : '';

  return (
    <div className={`accordion__title button--accordion theme-transparent ${activeClassName}`}>
      <p className={styles.fontSizeAndNoMargin}>{translate(`inLanguage.${lang}`)}</p>
    </div>
  );
};

const createAccordion = (translate, element, userLang) => {
  const userLangIndex = AVAILABLE_LANGUAGES.includes(userLang)
    ? AVAILABLE_LANGUAGES.findIndex((v) => v === userLang) : 0;

  return (
    <div>
      <ul className="accordion">
        {
          AVAILABLE_LANGUAGES.map((lang, idx) => (
            <Collapsible
              open={idx === userLangIndex}
              trigger={createAccordionTitle(lang, false, translate)}
              triggerWhenOpen={createAccordionTitle(lang, true, translate)}
              key={lang}
            >
              <li className="accordion__item" key={lang}>
                {element(lang)}
              </li>
            </Collapsible>
          ))
        }
      </ul>
    </div>
  );
};

const createLocalizedInputs = (element) => (
  <div>
    {AVAILABLE_LANGUAGES.map((lang) => (
      <div key={lang} className={styles.formItem}>{element(lang)}</div>))}
  </div>
);

const createInput = (
  name,
  translate,
  placeholderTranslationNamespace,
  onInputChange,
  values,
  maxLength,
  disabled,
  dataCyPrefix
) => (lang) => (
  <TextInput
    name={name}
    maxLength={maxLength}
    placeholder={translate(`${placeholderTranslationNamespace}.${lang}`)}
    onChange={onInputChange(lang)}
    value={values[lang] || ''}
    disabled={disabled}
    dataCy={`${dataCyPrefix}-${lang}`}
  />
);

const createTextarea = (
  name,
  translate,
  placeholderTranslationNamespace,
  onInputChange,
  values,
  rows,
  disabled,
  dataCyPrefix
) => (lang) => (
  <textarea
    name={name}
    placeholder={translate(`${placeholderTranslationNamespace}.${lang}`)}
    onChange={onInputChange(lang)}
    value={values[lang] || ''}
    rows={rows}
    disabled={disabled}
    data-cy={`${dataCyPrefix}-${lang}`}
  />
);

const createRichTextarea = (
  name,
  translate,
  placeholderTranslationNamespace,
  onInputChange,
  values,
  hideButtonsOnBlur,
  disabled,
  externallyChangedValues
) => (lang) => (
  <RichTextEditor
    name={name}
    placeholder={translate(`${placeholderTranslationNamespace}.${lang}`)}
    onChange={onInputChange(lang, name)}
    defaultValue={values[lang] || ''}
    externallyChangedValue={externallyChangedValues[lang]}
    hideButtonsOnBlur={hideButtonsOnBlur}
    disabled={disabled}
  />
);

const LocalizedInputComponent = ({
  name,
  values,
  onInputChange,
  placeholderTranslationNamespace,
  translate,
  lang,
  useAccordion,
  maxLength,
  disabled,
  dataCyPrefix
}) => {
  const input = createInput(
    name,
    translate,
    placeholderTranslationNamespace,
    onInputChange,
    values,
    maxLength,
    disabled,
    dataCyPrefix
  );
  if (useAccordion) {
    return (createAccordion(translate, input, lang));
  }
  return (createLocalizedInputs(input));
};

const LocalizedTextAreaComponent = ({
  name,
  values,
  rows,
  onInputChange,
  placeholderTranslationNamespace,
  translate,
  lang,
  useAccordion,
  disabled,
  dataCyPrefix
}) => {
  const textarea = createTextarea(
    name,
    translate,
    placeholderTranslationNamespace,
    onInputChange,
    values,
    rows,
    disabled,
    dataCyPrefix
  );
  if (useAccordion) {
    return (createAccordion(translate, textarea, lang));
  }
  return (createLocalizedInputs(textarea));
};

const LocalizedRichTextAreaComponent = ({
  name,
  values,
  onInputChange,
  placeholderTranslationNamespace,
  hideButtonsOnBlur,
  translate,
  lang,
  useAccordion,
  disabled,
  externallyChangedValues, dataCyPrefix
}) => {
  const richTextarea = createRichTextarea(
    name,
    translate,
    placeholderTranslationNamespace,
    onInputChange,
    values,
    hideButtonsOnBlur,
    disabled,
    externallyChangedValues, dataCyPrefix
  );
  if (useAccordion) {
    return (createAccordion(translate, richTextarea, lang));
  }
  return (createLocalizedInputs(richTextarea));
};

const propTypes = {
  name: string.isRequired,
  values: localizedType.isRequired,
  onInputChange: func.isRequired,
  placeholderTranslationNamespace: string.isRequired,
  useAccordion: bool,
  ...langType
};
const inputPropTypes = {
  ...propTypes,
  maxLength: number.isRequired
};
const textAreaPropTypes = {
  ...propTypes,
  rows: number
};
const richTextAreaPropTypes = {
  ...propTypes,
  hideButtonsOnBlur: bool,
  externallyChangedValues: localizedType
};

const defaults = {
  // linter doesn't recognize that useAccordion has corresponding propType defined
  // eslint-disable-next-line react/default-props-match-prop-types
  useAccordion: false
};
const textAreaDefaults = {
  ...defaults,
  rows: 5
};
const richTextAreaDefaults = {
  ...defaults,
  hideButtonsOnBlur: true,
  externallyChangedValues: {}
};

LocalizedInputComponent.propTypes = inputPropTypes;
LocalizedInputComponent.defaultProps = defaults;
LocalizedTextAreaComponent.propTypes = textAreaPropTypes;
LocalizedTextAreaComponent.defaultProps = textAreaDefaults;
LocalizedRichTextAreaComponent.propTypes = richTextAreaPropTypes;
LocalizedRichTextAreaComponent.defaultProps = richTextAreaDefaults;

export const LocalizedInput = withLang(LocalizedInputComponent);
export const LocalizedTextArea = withLang(LocalizedTextAreaComponent);
export const LocalizedRichTextArea = withLang(LocalizedRichTextAreaComponent);
export default LocalizedInput;
